var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as _ from "lodash";
import { event, lifecycle } from "vscode-lib";
import * as Y from "yjs";
const DEFAULT_OPTIONS = {
    // throttle flushing write events to matrix by 500ms
    flushInterval: process.env.NODE_ENV === "test" ? 100 : 100 * 5,
    // if writing to the room fails, wait 30 seconds before retrying
    retryIfForbiddenInterval: 1000 * 30,
};
/**
 * A class that writes updates in the form of Uint8Array to Matrix.
 */
export class ThrottledMatrixWriter extends lifecycle.Disposable {
    constructor(matrixClient, translator, opts = {}) {
        super();
        this.matrixClient = matrixClient;
        this.translator = translator;
        this.pendingUpdates = [];
        this.isSendingUpdates = false;
        this._canWrite = true;
        this._onCanWriteChanged = this._register(new event.Emitter());
        this.onCanWriteChanged = this._onCanWriteChanged.event;
        this._onSentAllEvents = this._register(new event.Emitter());
        this.onSentAllEvents = this._onSentAllEvents.event;
        this.flushUpdatesToMatrix = () => __awaiter(this, void 0, void 0, function* () {
            if (this.isSendingUpdates || !this.pendingUpdates.length) {
                return;
            }
            if (!this.roomId) {
                // we're still initializing. We'll flush updates again once we're initialized
                return;
            }
            this.isSendingUpdates = true;
            const merged = Y.mergeUpdates(this.pendingUpdates);
            this.pendingUpdates = [];
            let retryImmediately = false;
            try {
                console.log("Sending updates");
                yield this.translator.sendUpdate(this.matrixClient, this.roomId, merged);
                this.setCanWrite(true);
                console.log("sent updates");
            }
            catch (e) {
                if (e.errcode === "M_FORBIDDEN") {
                    console.warn("not allowed to edit document", e);
                    this.setCanWrite(false);
                    try {
                        // make sure we're in the room, so we can send updates
                        // guests can't / won't join, so MatrixProvider won't send updates for this room
                        yield this.matrixClient.joinRoom(this.roomId);
                        console.log("joined room", this.roomId);
                        retryImmediately = true;
                    }
                    catch (e) {
                        console.warn("failed to join room", e);
                    }
                }
                else {
                    console.error("error sending updates", e);
                }
                this.pendingUpdates.unshift(merged);
            }
            finally {
                this.isSendingUpdates = false;
            }
            if (this.pendingUpdates.length) {
                // if new events have been added in the meantime (or we need to retry)
                this.retryTimeoutHandler = setTimeout(() => {
                    this.throttledFlushUpdatesToMatrix();
                }, retryImmediately
                    ? 0
                    : this.canWrite
                        ? this.opts.flushInterval
                        : this.opts.retryIfForbiddenInterval);
            }
            else {
                console.log("_onSentAllEvents");
                this._onSentAllEvents.fire();
            }
        });
        this.opts = Object.assign(Object.assign({}, DEFAULT_OPTIONS), opts);
        this.throttledFlushUpdatesToMatrix = _.throttle(this.flushUpdatesToMatrix, this.canWrite
            ? this.opts.flushInterval
            : this.opts.retryIfForbiddenInterval);
    }
    setCanWrite(value) {
        if (this._canWrite !== value) {
            this._canWrite = value;
            this._onCanWriteChanged.fire();
        }
    }
    initialize(roomId) {
        return __awaiter(this, void 0, void 0, function* () {
            this.roomId = roomId;
            this.throttledFlushUpdatesToMatrix();
        });
    }
    get canWrite() {
        return this._canWrite;
    }
    writeUpdate(update) {
        this.pendingUpdates.push(update);
        this.throttledFlushUpdatesToMatrix();
    }
    // Helper method that's mainly used in unit tests
    waitForFlush() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.pendingUpdates.length && !this.isSendingUpdates) {
                return;
            }
            yield event.Event.toPromise(this.onSentAllEvents);
        });
    }
    dispose() {
        super.dispose();
        clearTimeout(this.retryTimeoutHandler);
        this.throttledFlushUpdatesToMatrix.cancel();
    }
}
//# sourceMappingURL=ThrottledMatrixWriter.js.map